import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Drawer, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LockIcon from "@mui/icons-material/Lock";
import styles from "./MarketData.module.css";
import { fetchWithAuth } from "../../utils/ApiUtils";
import { useAuth } from "../../context/AuthContext";
import { formatCurrency, getDisplayPrice } from "../../utils/priceUtils";

const RETAILER_LABELS = {
    BEST_BUY: "Best Buy",
    MICRO_CENTER: "Micro Center",
    NEWEGG: "Newegg",
};

const RETAILER_ORDER = ["BEST_BUY", "MICRO_CENTER", "NEWEGG"];

const RANGES = [
    { key: "1M", days: 30 },
    { key: "3M", days: 90 },
    { key: "All", days: null },
];

// Current API timestamps are ISO-8601 instants. Keep array support for
// responses from older app instances during a rolling deployment.
const parseApiDate = (value) => {
    if (!value) return null;
    if (Array.isArray(value)) {
        const [year, month, day, hour = 0, minute = 0, second = 0] = value;
        return new Date(Date.UTC(year, month - 1, day, hour, minute, second));
    }
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatRelative = (date, now) => {
    if (!date) return null;
    const diffMs = now - date.getTime();
    const minutes = Math.floor(diffMs / 60000);
    if (minutes < 1) return "just now";
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 30) return `${days}d ago`;
    return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

const formatEventDate = (date) => {
    if (!date) return "—";
    return date.toLocaleDateString(undefined, { month: "short", day: "numeric" })
        + " · "
        + date.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
};

const CHART_DEFAULT_WIDTH = 1060;
const CHART_HEIGHT = 230;
const CHART_PAD = { top: 16, right: 14, bottom: 30, left: 64 };
// Inline style so page-level svg/text CSS can't override the sizing.
const CHART_TEXT_STYLE = { fontSize: "11px", fontFamily: "inherit" };
const retailerSeriesClass = (retailer) => {
    if (retailer === "MICRO_CENTER") return styles.series_micro_center;
    if (retailer === "NEWEGG") return styles.series_newegg;
    return styles.series_best_buy;
};
const retailerFillClass = (retailer) => {
    if (retailer === "MICRO_CENTER") return styles.fill_micro_center;
    if (retailer === "NEWEGG") return styles.fill_newegg;
    return styles.fill_best_buy;
};

export const MarketData = ({ stock }) => {
    const { isPremium, authToken, loading: authLoading } = useAuth();
    const [marketData, setMarketData] = useState(null);
    const [range, setRange] = useState("All");
    const [hover, setHover] = useState(null);
    const [dropLogOpen, setDropLogOpen] = useState(false);
    const [chartWidth, setChartWidth] = useState(CHART_DEFAULT_WIDTH);
    const [relativeNow, setRelativeNow] = useState(() => Date.now());
    const chartContainerRef = useRef(null);

    const sku = stock?.sku;

    useEffect(() => {
        setMarketData(null);
        if (!sku || authLoading) return undefined;

        let cancelled = false;
        const controller = new AbortController();
        const load = async () => {
            try {
                const response = await fetchWithAuth(`/api/v1/stocks/sku/${sku}/market-data`, {
                    signal: controller.signal,
                });
                if (!response.ok) return;
                const apiResponse = await response.json();
                if (
                    !cancelled
                    && Number(apiResponse?.data?.sku) === Number(sku)
                ) {
                    setMarketData(apiResponse.data);
                }
            } catch (error) {
                if (error?.name === "AbortError") return;
                // Market data is progressive enhancement; the page works without it.
                console.warn("Market data unavailable:", error);
            }
        };
        load();
        return () => {
            cancelled = true;
            controller.abort();
        };
    }, [sku, authToken, isPremium, authLoading]);

    useEffect(() => {
        setRelativeNow(Date.now());
        if (!marketData?.lastRestockedAt) return undefined;

        const intervalId = window.setInterval(() => setRelativeNow(Date.now()), 60000);
        return () => window.clearInterval(intervalId);
    }, [marketData?.lastRestockedAt]);

    useLayoutEffect(() => {
        const container = chartContainerRef.current;
        if (!container) return undefined;

        const updateWidth = () => {
            if (container.clientWidth > 0) {
                setChartWidth(Math.max(320, Math.round(container.clientWidth)));
            }
        };
        updateWidth();

        if (typeof ResizeObserver !== "undefined") {
            const observer = new ResizeObserver(updateWidth);
            observer.observe(container);
            return () => observer.disconnect();
        }

        window.addEventListener("resize", updateWidth);
        return () => window.removeEventListener("resize", updateWidth);
    }, [marketData]);

    const bestBuyPricing = {
        regularPrice: stock?.bestBuyRegularPrice ?? stock?.regularPrice,
        salePrice: stock?.bestBuySalePrice ?? stock?.salePrice,
        onSale: stock?.bestBuyOnSale ?? stock?.onSale,
    };
    const regularPrice = Number(bestBuyPricing.regularPrice) > 0
        ? Number(bestBuyPricing.regularPrice)
        : null;
    const currentPrice = getDisplayPrice(bestBuyPricing);

    const priceChangesByRetailer = useMemo(() => {
        const grouped = {
            BEST_BUY: [],
            MICRO_CENTER: [],
            NEWEGG: [],
        };

        (marketData?.priceHistory || []).forEach((point) => {
            // Rows created before the retailer rollout are Best Buy rows. The
            // fallback also keeps the chart useful during a rolling deploy.
            const retailer = point.retailer || "BEST_BUY";
            const date = parseApiDate(point.detectedAt);
            const oldPrice = Number(point.oldPrice);
            const newPrice = Number(point.newPrice);
            if (!grouped[retailer] || !date || !(newPrice > 0)) return;

            grouped[retailer].push({
                oldPrice: oldPrice > 0 ? oldPrice : newPrice,
                newPrice,
                date,
            });
        });

        RETAILER_ORDER.forEach((retailer) => {
            grouped[retailer].sort((a, b) => a.date - b.date);
        });
        return grouped;
    }, [marketData]);

    const restockEvents = useMemo(() => {
        if (!marketData?.restockEvents) return [];
        return marketData.restockEvents
            .map((event) => ({ ...event, date: parseApiDate(event.detectedAt) }))
            .filter((event) => event.date)
            .sort((a, b) => b.date - a.date);
    }, [marketData]);

    const lastRestockedAt = parseApiDate(marketData?.lastRestockedAt);
    const microCenterCurrentPrice = getDisplayPrice(stock?.mcOffer);
    const neweggOffer = Array.isArray(stock?.retailerOffers)
        ? stock.retailerOffers.find((offer) => offer?.retailer === "NEWEGG")
        : null;
    const neweggCurrentPrice = getDisplayPrice(neweggOffer);

    const chart = useMemo(() => {
        const now = Date.now();
        const rangeDef = RANGES.find((r) => r.key === range) || RANGES[1];
        const allPriceChanges = RETAILER_ORDER.flatMap(
            (retailer) => priceChangesByRetailer[retailer]
        );
        let start;
        if (rangeDef.days) {
            start = now - rangeDef.days * 86400000;
        } else {
            const observedTimes = [
                ...allPriceChanges.map((point) => point.date.getTime()),
                ...restockEvents.map((event) => event.date.getTime()),
            ];
            const earliest = observedTimes.length ? Math.min(...observedTimes) : now;
            start = Math.min(earliest, now - 30 * 86400000);
        }

        const seriesModels = RETAILER_ORDER.map((retailer) => {
            const points = priceChangesByRetailer[retailer];
            if (!points.length) return null;

            const before = points.filter((point) => point.date.getTime() < start);
            const inRange = points.filter((point) => {
                const detectedAt = point.date.getTime();
                return detectedAt >= start && detectedAt <= now;
            });

            if (!before.length && !inRange.length) return null;

            const lineStartTime = before.length ? start : inRange[0].date.getTime();
            const startPrice = before.length
                ? before[before.length - 1].newPrice
                : inRange[0].oldPrice;

            const priceAt = (time) => {
                if (time < lineStartTime) return null;
                let value = startPrice;
                for (const point of inRange) {
                    if (point.date.getTime() > time) break;
                    value = point.newPrice;
                }
                return value > 0 ? value : null;
            };

            return {
                retailer,
                lineStartTime,
                startPrice,
                points: inRange,
                priceAt,
                prices: [startPrice, ...inRange.map((point) => point.newPrice)]
                    .filter((price) => price > 0),
            };
        }).filter(Boolean);

        const seriesByRetailer = Object.fromEntries(
            seriesModels.map((series) => [series.retailer, series])
        );
        const restocksInRange = restockEvents.filter((event) => {
            const detectedAt = event.date.getTime();
            return (
                RETAILER_ORDER.includes(event.retailer)
                && detectedAt >= start
                && detectedAt <= now
            );
        });

        const dotModels = restocksInRange.map((event) => {
            const eventTime = event.date.getTime();
            const seriesPrice = seriesByRetailer[event.retailer]?.priceAt(eventTime);
            const eventPrice = Number(event.price);
            const price = seriesPrice > 0
                ? seriesPrice
                : (eventPrice > 0 ? eventPrice : null);
            return price ? { event, price } : null;
        }).filter(Boolean);

        const prices = [
            ...seriesModels.flatMap((series) => series.prices),
            ...dotModels.map((dot) => dot.price),
            seriesByRetailer.BEST_BUY || !seriesModels.length ? currentPrice : null,
            seriesByRetailer.MICRO_CENTER ? microCenterCurrentPrice : null,
            seriesByRetailer.NEWEGG ? neweggCurrentPrice : null,
            regularPrice,
        ]
            .filter((value) => value > 0);
        if (!prices.length) return null;

        const minPrice = Math.min(...prices);
        const maxPrice = Math.max(...prices);
        const spread = Math.max(maxPrice - minPrice, maxPrice * 0.05, 1);
        const yMin = minPrice - spread * 0.15;
        const yMax = maxPrice + spread * 0.15;

        const plotW = chartWidth - CHART_PAD.left - CHART_PAD.right;
        const plotH = CHART_HEIGHT - CHART_PAD.top - CHART_PAD.bottom;
        const x = (t) => CHART_PAD.left + ((t - start) / Math.max(now - start, 1)) * plotW;
        const y = (price) => CHART_PAD.top + (1 - (price - yMin) / (yMax - yMin)) * plotH;

        const series = seriesModels.map((model) => {
            let path = `M ${x(model.lineStartTime)} ${y(model.startPrice)}`;
            model.points.forEach((point) => {
                const pointX = x(point.date.getTime());
                path += ` H ${pointX} V ${y(point.newPrice)}`;
            });
            path += ` H ${chartWidth - CHART_PAD.right}`;
            return { ...model, path };
        });

        const dots = dotModels.map(({ event, price }) => ({
            retailer: event.retailer,
            cx: x(event.date.getTime()),
            cy: y(price),
        }));

        const gridPrices = [yMax - (yMax - yMin) * 0.2, yMax - (yMax - yMin) * 0.5, yMax - (yMax - yMin) * 0.8];

        const midDate = new Date((start + now) / 2);
        const startDate = new Date(start);

        const timeAt = (px) => start + ((px - CHART_PAD.left) / Math.max(plotW, 1)) * (now - start);

        return {
            series,
            dots,
            regularPriceY: regularPrice ? y(regularPrice) : null,
            hasObservedHistory: series.length > 0,
            gridLines: gridPrices.map((price) => ({ y: y(price), label: formatCurrency(price) })),
            xLabels: [
                { x: CHART_PAD.left, label: startDate.toLocaleDateString(undefined, { month: "short", day: "numeric" }) },
                { x: CHART_PAD.left + plotW / 2, label: midDate.toLocaleDateString(undefined, { month: "short", day: "numeric" }) },
                { x: chartWidth - CHART_PAD.right, label: "Today" },
            ],
            timeAt,
            yFor: y,
            plotTop: CHART_PAD.top,
            plotBottom: CHART_HEIGHT - CHART_PAD.bottom,
        };
    }, [priceChangesByRetailer, restockEvents, currentPrice, microCenterCurrentPrice, neweggCurrentPrice, regularPrice, range, chartWidth]);

    const handleChartPointer = useCallback((event) => {
        if (!chart?.hasObservedHistory) return;
        const rect = event.currentTarget.getBoundingClientRect();
        if (rect.width <= 0) return;
        const svgX = ((event.clientX - rect.left) / rect.width) * chartWidth;
        const clampedX = Math.min(Math.max(svgX, CHART_PAD.left), chartWidth - CHART_PAD.right);
        const time = chart.timeAt(clampedX);
        const rows = chart.series.map((series) => {
            const price = series.priceAt(time);
            return price > 0
                ? { retailer: series.retailer, price, y: chart.yFor(price) }
                : null;
        }).filter(Boolean);
        if (!rows.length) {
            setHover(null);
            return;
        }
        setHover({
            x: clampedX,
            y: Math.min(...rows.map((row) => row.y)),
            rows,
            date: new Date(time),
        });
    }, [chart, chartWidth]);

    const clearChartPointer = useCallback(() => setHover(null), []);

    if (!sku || !marketData || Number(marketData.sku) !== Number(sku)) {
        return null;
    }

    const restocks30d = marketData.restocksLast30Days ?? 0;
    const priceVsRegular = regularPrice && currentPrice
        ? (currentPrice < regularPrice
            ? "Below regular price"
            : (currentPrice === regularPrice ? "At regular price" : "Above regular price"))
        : null;

    return (
        <div className={styles.container}>
            <h2 className={styles.heading}>Market data</h2>

            <div className={styles.tiles}>
                <div className={styles.tile}>
                    <div className={styles.tile_label}>Last restocked</div>
                    <div className={`${styles.tile_value} ${lastRestockedAt ? styles.tile_value_positive : ""}`}>
                        {lastRestockedAt ? formatRelative(lastRestockedAt, relativeNow) : "—"}
                    </div>
                    <div className={styles.tile_sub}>
                        {lastRestockedAt
                            ? `${RETAILER_LABELS[marketData.lastRestockRetailer] || ""} · ${marketData.lastRestockChannel === "IN_STORE" ? "in-store" : "online"}`
                            : "Tracking just launched"}
                    </div>
                </div>
                <div className={styles.tile}>
                    <div className={styles.tile_label}>Restocks · 30 days</div>
                    <div className={styles.tile_value}>{restocks30d}</div>
                    <div className={styles.tile_sub}>
                        {`${marketData.onlineRestocksLast30Days ?? 0} online · ${marketData.inStoreRestocksLast30Days ?? 0} in-store`}
                    </div>
                </div>
                <div className={styles.tile}>
                    <div className={styles.tile_label}>Best Buy price</div>
                    <div className={styles.tile_value}>{currentPrice ? formatCurrency(currentPrice) : "—"}</div>
                    {priceVsRegular && (
                        <div className={`${styles.tile_sub} ${currentPrice <= regularPrice ? styles.tile_value_positive : ""}`}>
                            {priceVsRegular}
                        </div>
                    )}
                </div>
                <div className={styles.tile}>
                    <div className={styles.tile_label}>Watching</div>
                    <div className={styles.tile_value}>{marketData.trackerCount ?? 0}</div>
                    <div className={styles.tile_sub}>StockMaid users tracking this</div>
                </div>
            </div>

            {chart && (
                <div className={styles.panel}>
                    <div className={styles.panel_header}>
                        <div>
                            <div className={styles.panel_title}>Price history</div>
                            {chart.series.length > 0 && (
                                <div className={styles.legend} aria-label="Price history retailers">
                                    {chart.series.map((series) => (
                                        <span className={styles.legend_item} key={series.retailer}>
                                            <span
                                                className={`${styles.legend_swatch} ${retailerFillClass(series.retailer)}`}
                                                aria-hidden="true"
                                            />
                                            {RETAILER_LABELS[series.retailer]}
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className={styles.panel_actions}>
                            <div className={styles.range_tabs} role="group" aria-label="Price history range">
                                {RANGES.map((r) => (
                                    <button
                                        key={r.key}
                                        type="button"
                                        className={`${styles.range_tab} ${range === r.key ? styles.range_tab_active : ""}`}
                                        onClick={() => setRange(r.key)}
                                        aria-pressed={range === r.key}
                                    >
                                        {r.key}
                                    </button>
                                ))}
                            </div>
                            <button
                                type="button"
                                className={styles.view_drops}
                                onClick={() => setDropLogOpen(true)}
                            >
                                View drop log ›
                            </button>
                        </div>
                    </div>
                    <div className={styles.chart_wrap} ref={chartContainerRef}>
                        <svg
                            className={styles.chart}
                            viewBox={`0 0 ${chartWidth} ${CHART_HEIGHT}`}
                            role="img"
                            aria-label={`Retail price history for ${chart.series.map((series) => RETAILER_LABELS[series.retailer]).join(" and ") || "tracked retailers"} with ${chart.dots.length} restock markers`}
                            onPointerMove={handleChartPointer}
                            onPointerDown={handleChartPointer}
                            onPointerLeave={clearChartPointer}
                        >
                        {chart.gridLines.map((line, index) => (
                            <g key={index}>
                                <line
                                    x1={CHART_PAD.left}
                                    y1={line.y}
                                    x2={chartWidth - CHART_PAD.right}
                                    y2={line.y}
                                    stroke="var(--sm-border-color)"
                                    strokeWidth="1"
                                />
                                <text
                                    x={CHART_PAD.left - 8}
                                    y={line.y + 3}
                                    textAnchor="end"
                                    style={CHART_TEXT_STYLE}
                                    fill="var(--text-color-secondary)"
                                >
                                    {line.label}
                                </text>
                            </g>
                        ))}
                        {chart.regularPriceY !== null && (
                            <g>
                                <line
                                    x1={CHART_PAD.left}
                                    y1={chart.regularPriceY}
                                    x2={chartWidth - CHART_PAD.right}
                                    y2={chart.regularPriceY}
                                    stroke="var(--text-color-secondary)"
                                    strokeWidth="1.5"
                                    strokeDasharray="5 4"
                                />
                                <text
                                    x={chartWidth - CHART_PAD.right}
                                    y={chart.regularPriceY + 14}
                                    textAnchor="end"
                                    style={CHART_TEXT_STYLE}
                                    fill="var(--text-color-secondary)"
                                >
                                    {`Regular ${formatCurrency(regularPrice)}`}
                                </text>
                            </g>
                        )}
                        {chart.series.map((series) => (
                            <path
                                key={series.retailer}
                                data-testid={`price-series-${series.retailer}`}
                                className={retailerSeriesClass(series.retailer)}
                                d={series.path}
                                fill="none"
                                strokeWidth="2"
                            />
                        ))}
                        {chart.dots.map((dot, index) => (
                            <circle
                                key={`${dot.retailer}-${index}`}
                                data-testid={`restock-dot-${dot.retailer}`}
                                className={retailerFillClass(dot.retailer)}
                                cx={dot.cx}
                                cy={dot.cy}
                                r="4"
                            />
                        ))}
                        {chart.xLabels.map((label, index) => (
                            <text
                                key={index}
                                x={label.x}
                                y={CHART_HEIGHT - 8}
                                textAnchor={index === 0 ? "start" : (index === 2 ? "end" : "middle")}
                                style={CHART_TEXT_STYLE}
                                fill="var(--text-color-secondary)"
                            >
                                {label.label}
                            </text>
                        ))}
                        {hover && (
                            <g>
                                <line
                                    x1={hover.x}
                                    y1={chart.plotTop}
                                    x2={hover.x}
                                    y2={chart.plotBottom}
                                    stroke="var(--text-color-secondary)"
                                    strokeWidth="1"
                                />
                                {hover.rows.map((row) => (
                                    <circle
                                        key={row.retailer}
                                        className={retailerFillClass(row.retailer)}
                                        cx={hover.x}
                                        cy={row.y}
                                        r="5"
                                        stroke="var(--background-color-light)"
                                        strokeWidth="2"
                                    />
                                ))}
                            </g>
                        )}
                        </svg>
                        {hover && (
                            <div
                                className={styles.chart_tooltip}
                                role="tooltip"
                                style={{
                                    left: `${Math.min(Math.max((hover.x / chartWidth) * 100, 9), 91)}%`,
                                    top: `${(hover.y / CHART_HEIGHT) * 100}%`,
                                }}
                            >
                                <div className={styles.chart_tooltip_date}>
                                    {hover.date.toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })}
                                </div>
                                {hover.rows.map((row) => (
                                    <div className={styles.chart_tooltip_row} key={row.retailer}>
                                        <span
                                            className={`${styles.chart_tooltip_swatch} ${retailerFillClass(row.retailer)}`}
                                            aria-hidden="true"
                                        />
                                        <span>{RETAILER_LABELS[row.retailer]}</span>
                                        <strong>{formatCurrency(row.price)}</strong>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    <div className={styles.chart_note}>
                        {!chart.hasObservedHistory ? "No recorded price changes yet · " : ""}
                        Dots mark restocks · retail price only, no marketplace sellers
                        {range === "All" && marketData.priceHistoryTruncated ? " · showing the latest 200 changes" : ""}
                    </div>
                </div>
            )}

            {!chart && (
                <div className={styles.panel}>
                    <div className={styles.panel_header}>
                        <div className={styles.panel_title}>Drop log</div>
                        <button
                            type="button"
                            className={styles.view_drops}
                            onClick={() => setDropLogOpen(true)}
                        >
                            View drop log ›
                        </button>
                    </div>
                </div>
            )}

            <Drawer
                anchor="right"
                open={dropLogOpen}
                onClose={() => setDropLogOpen(false)}
                PaperProps={{ sx: { width: { xs: "100%", sm: 560 } } }}
            >
                <div className={styles.drawer_content}>
                    <div className={styles.drawer_header}>
                        <div>
                            <div className={styles.drawer_title}>Drop log</div>
                            <div className={styles.drawer_subtitle}>
                                Every time this product came back in stock at Best Buy, Micro Center, or Newegg
                            </div>
                        </div>
                        <IconButton onClick={() => setDropLogOpen(false)} aria-label="Close drop log" size="small">
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </div>
                {restockEvents.length === 0 ? (
                    <div className={styles.empty}>
                        Restock tracking just launched for this product — drops will appear here as they happen.
                    </div>
                ) : (
                    <div className={styles.table_scroll}>
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    <th scope="col">Date</th>
                                    <th scope="col">Retailer</th>
                                    <th scope="col">Channel</th>
                                    <th scope="col">Price</th>
                                    <th scope="col" className={styles.table_states_col}>States</th>
                                </tr>
                            </thead>
                            <tbody>
                                {restockEvents.map((event, index) => (
                                    <tr key={index}>
                                        <td>{formatEventDate(event.date)}</td>
                                        <td>{RETAILER_LABELS[event.retailer] || event.retailer}</td>
                                        <td>
                                            <span className={`${styles.chip} ${event.channel === "IN_STORE" ? styles.chip_instore : styles.chip_online}`}>
                                                {event.channel === "IN_STORE" ? "In-store" : "Online"}
                                            </span>
                                        </td>
                                        <td>{event.price > 0 ? formatCurrency(event.price) : "—"}</td>
                                        <td className={styles.table_states_col}>
                                            {event.channel !== "IN_STORE" ? (
                                                <span className={styles.states_muted}>Nationwide</span>
                                            ) : isPremium ? (
                                                event.stateCodes?.length ? (
                                                    event.stateCodes.join(", ")
                                                ) : (
                                                    <span className={styles.states_muted}>
                                                        {event.stateCount > 0
                                                            ? `${event.stateCount} state${event.stateCount === 1 ? "" : "s"} · details unavailable`
                                                            : "Details unavailable"}
                                                    </span>
                                                )
                                            ) : event.stateCount > 0 ? (
                                                <span className={styles.states_muted}>
                                                    {`${event.stateCount} state${event.stateCount === 1 ? "" : "s"} · `}
                                                    <LockIcon sx={{ fontSize: 13, verticalAlign: "-2px" }} />{" "}
                                                    <Link to="/subscription" className={styles.premium_link}>Premium</Link>
                                                </span>
                                            ) : (
                                                <span className={styles.states_muted}>—</span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
                {!isPremium && (
                    <div className={styles.footer_row}>
                        <span>Premium shows which states get each in-store drop.</span>
                        <Link to="/subscription" className={styles.premium_link}>See state availability</Link>
                    </div>
                )}
                </div>
            </Drawer>
        </div>
    );
};

export default MarketData;
